/**
 * useTrialStatus + TrialBanner — Sprint 1D #12.
 *
 * Trial → paid conversion experience. Reads the current subscription
 * (React Query, 5min stale), computes days remaining, and surfaces a
 * dismissible banner that escalates in severity as the deadline approaches.
 *
 * The hook is in one file with the component so wiring is "drop in, import,
 * mount" — no extra service-layer changes needed since /subscriptions/current
 * already exists.
 *
 * Mount the banner ONCE in PageLayout (or App.tsx), above the page content.
 * It self-hides for trial states that aren't active, and for non-tenant_owner
 * roles (only the owner pays the bill).
 */
import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Clock, AlertTriangle, X, ArrowRight, Zap } from "lucide-react";
import api from "@/services/api";
import useAuthStore from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";

interface SubscriptionResponse {
  _id: string;
  status:
    | "active"
    | "trialing"
    | "trial"
    | "pending"
    | "cancelling"
    | "cancelled"
    | "grace_period"
    | "expired";
  trialEndsAt?: string | null;
  endDate?: string | null;
  planId?: { name?: string };
}

interface TrialStatus {
  isTrial: boolean;
  daysRemaining: number;
  hoursRemaining: number;
  trialEndsAt: Date | null;
  severity: "none" | "info" | "warning" | "critical" | "expired";
  planName: string | null;
}

// ─── Hook ──────────────────────────────────────────────────────────

export function useTrialStatus(): TrialStatus {
  // 13f: only people who can see the subscription ask for it (a cashier's
  // every page load was a refused request).
  const { can, ready } = usePermissions();
  const { data } = useQuery<SubscriptionResponse | null>({
    enabled: ready && can("subscription.read"),
    queryKey: ["subscription", "current"],
    queryFn: async () => {
      try {
        const res = await api.get("/subscriptions/current");
        return res.data?.data ?? null;
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return useMemo<TrialStatus>(() => {
    if (!data) {
      return {
        isTrial: false,
        daysRemaining: 0,
        hoursRemaining: 0,
        trialEndsAt: null,
        severity: "none",
        planName: null,
      };
    }

    const isTrial = data.status === "trial" || data.status === "trialing";

    // The field name varies by backend version
    const endRaw = data.trialEndsAt || data.endDate || null;
    if (!isTrial || !endRaw) {
      return {
        isTrial: false,
        daysRemaining: 0,
        hoursRemaining: 0,
        trialEndsAt: null,
        severity: "none",
        planName: data.planId?.name || null,
      };
    }

    const endsAt = new Date(endRaw);
    const msRemaining = endsAt.getTime() - Date.now();
    const hoursRemaining = Math.max(
      0,
      Math.floor(msRemaining / (60 * 60 * 1000)),
    );
    const daysRemaining = Math.max(
      0,
      Math.ceil(msRemaining / (24 * 60 * 60 * 1000)),
    );

    let severity: TrialStatus["severity"] = "info";
    if (msRemaining <= 0) severity = "expired";
    else if (daysRemaining <= 1) severity = "critical";
    else if (daysRemaining <= 4) severity = "warning";
    else if (daysRemaining <= 10) severity = "info";
    else severity = "none";

    return {
      isTrial: true,
      daysRemaining,
      hoursRemaining,
      trialEndsAt: endsAt,
      severity,
      planName: data.planId?.name || null,
    };
  }, [data]);
}

// ─── Banner component ──────────────────────────────────────────────

const STORAGE_KEY = "eaze:trial-banner-dismissed-day";

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export const TrialBanner: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const trial = useTrialStatus();

  // Per-day dismissal — a user can hide it once but it returns the next day.
  // Critical + expired states are NEVER dismissible (they need attention).
  const [dismissedDay, setDismissedDay] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    // Reset dismissed flag if it's a new day
    if (dismissedDay && dismissedDay !== todayKey()) {
      setDismissedDay(null);
    }
  }, [dismissedDay]);

  const handleDismiss = () => {
    const key = todayKey();
    setDismissedDay(key);
    try {
      localStorage.setItem(STORAGE_KEY, key);
    } catch {
      /* ignore */
    }
  };

  // Visibility rules:
  //   - Only tenant_owner sees the banner (they pay)
  //   - Trial must be active
  //   - Severity must be info+
  //   - Banner is dismissed today (unless critical or expired)
  if (!user || user.role !== "tenant_owner") return null;
  if (!trial.isTrial) return null;
  if (trial.severity === "none") return null;

  const isPersistent =
    trial.severity === "critical" || trial.severity === "expired";
  if (!isPersistent && dismissedDay === todayKey()) return null;

  const expired = trial.severity === "expired";

  return (
    <div
      role="alert"
      className={[
        "relative w-full px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium",
        expired
          ? "bg-rose-600 text-white"
          : trial.severity === "critical"
            ? "bg-rose-500 text-white"
            : trial.severity === "warning"
              ? "bg-amber-500 text-white"
              : "bg-[#0A2540] text-white",
      ].join(" ")}
    >
      <div className="flex items-center gap-2 flex-1 justify-center max-w-4xl">
        {expired ? (
          <AlertTriangle className="h-4 w-4 shrink-0" />
        ) : trial.severity === "critical" ? (
          <AlertTriangle className="h-4 w-4 shrink-0" />
        ) : (
          <Clock className="h-4 w-4 shrink-0" />
        )}
        <span className="flex-1 text-center sm:text-left">
          {messageFor(trial)}
        </span>
        <Link
          to="/subscriptions"
          className="inline-flex items-center gap-1 rounded-md bg-white/15 hover:bg-white/25 transition-colors px-3 py-1 text-xs font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <Zap className="h-3 w-3" />
          {expired ? "Reactivate now" : "Upgrade to paid"}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {!isPersistent && (
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-white/70 hover:bg-white/10 hover:text-white"
          aria-label="Dismiss for today"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

function messageFor(t: TrialStatus): string {
  if (t.severity === "expired") {
    return "Your trial has ended. Reactivate to keep using Eaze without interruption.";
  }
  if (t.severity === "critical") {
    if (t.daysRemaining === 0) {
      return `Trial ends in ${t.hoursRemaining} hour${
        t.hoursRemaining === 1 ? "" : "s"
      }. Upgrade now to avoid losing access.`;
    }
    return "Last day of trial — upgrade today to keep your data accessible.";
  }
  if (t.severity === "warning") {
    return `${t.daysRemaining} days left in trial. Pick a plan to keep things running.`;
  }
  return `${t.daysRemaining} days left in your Eaze trial.`;
}

export default TrialBanner;
